import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { Loader2, Plus, Trash2, UserCog } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function TeamManagement() {
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [newRole, setNewRole] = useState<"admin" | "member">("member");

    const { data: members, isLoading, refetch } = trpc.team.getMembers.useQuery();
    const addMemberMutation = trpc.team.addMember.useMutation();
    const removeMemberMutation = trpc.team.removeMember.useMutation();
    const updateRoleMutation = trpc.team.updateRole.useMutation();

    const handleAddMember = async () => {
        if (!newEmail) {
            toast.error("請輸入電子郵件");
            return;
        }

        try {
            await addMemberMutation.mutateAsync({ email: newEmail, role: newRole });
            toast.success("成員已新增");
            setAddDialogOpen(false);
            setNewEmail("");
            setNewRole("member");
            refetch();
        } catch (error: any) {
            toast.error(error.message || "新增失敗");
        }
    };

    const handleRemoveMember = async (id: number) => {
        if (!confirm("確定要移除此成員嗎？")) return;

        try {
            await removeMemberMutation.mutateAsync({ id });
            toast.success("成員已移除");
            refetch();
        } catch (error: any) {
            toast.error(error.message || "移除失敗");
        }
    };

    const handleToggleRole = async (id: number, currentRole: string) => {
        const newRole = currentRole === "admin" ? "member" : "admin";

        try {
            await updateRoleMutation.mutateAsync({ id, role: newRole });
            toast.success("角色已更新");
            refetch();
        } catch (error: any) {
            toast.error(error.message || "更新失敗");
        }
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="container py-8 space-y-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-4xl font-bold mb-2">團隊管理</h1>
                        <p className="text-muted-foreground">
                            管理有權存取此儀表板的團隊成員
                        </p>
                    </div>
                    <Button onClick={() => setAddDialogOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        新增成員
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>團隊成員</CardTitle>
                        <CardDescription>
                            共 {members?.length || 0} 位成員
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>電子郵件</TableHead>
                                    <TableHead>角色</TableHead>
                                    <TableHead>加入日期</TableHead>
                                    <TableHead className="text-right">操作</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {members?.map((member: any) => (
                                    <TableRow key={member.id}>
                                        <TableCell className="font-medium">{member.email}</TableCell>
                                        <TableCell>
                                            <Badge variant={member.role === "admin" ? "default" : "secondary"}>
                                                {member.role === "admin" ? "管理員" : "成員"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(member.createdAt).toLocaleDateString('zh-TW')}
                                        </TableCell>
                                        <TableCell className="text-right space-x-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleToggleRole(member.id, member.role)}
                                                disabled={updateRoleMutation.isPending}
                                            >
                                                <UserCog className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemoveMember(member.id)}
                                                disabled={removeMemberMutation.isPending}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Add Member Dialog */}
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>新增團隊成員</DialogTitle>
                            <DialogDescription>
                                輸入成員的電子郵件地址以授予存取權限
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">電子郵件</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="example@gmail.com"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="role">角色</Label>
                                <Select value={newRole} onValueChange={(v) => setNewRole(v as "admin" | "member")}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="member">成員</SelectItem>
                                        <SelectItem value="admin">管理員</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                                取消
                            </Button>
                            <Button onClick={handleAddMember} disabled={addMemberMutation.isPending}>
                                {addMemberMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                新增
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
